import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useVCardStore } from '../../../store/vCardStore'
import { useAuthStore } from '../../../store/authStore'
import { db } from '../../../services/firebase.js'
import { doc, updateDoc, addDoc, collection, serverTimestamp, query, where, getDocs, getDoc } from 'firebase/firestore'
import TemplateSelector from '../components/TemplateSelector'
import ColorPicker from '../components/ColorPicker'
import SocialLinksForm from '../components/SocialLinksForm'
import ProfilePhotoForm from '../components/ProfilePhotoForm'
import ProductsForm from '../components/ProductsForm'
import ServicesForm from '../components/ServicesForm'
import GalleryForm from '../components/GalleryForm'
import Button from '../../../components/common/Button'
import { buildPublicUrl, getPublicSlugPrefix } from '../../../utils/url'
import LoadingSpinner from '../../../components/common/LoadingSpinner'
import toast from 'react-hot-toast'

function VCardEditorPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const location = useLocation()
  const { user } = useAuthStore()
  const { 
    currentVCard, 
    loading, 
    error, 
    fetchVCardById, 
    createVCard, 
    updateVCard,
    deleteVCard
  } = useVCardStore()

  const { 
    register, 
    handleSubmit, 
    formState: { errors }, 
    setValue, 
    watch,
    reset 
  } = useForm()

  const [selectedTemplate, setSelectedTemplate] = useState('template-1')
  const [primaryColor, setPrimaryColor] = useState('#2563eb')
  const [accentColor, setAccentColor] = useState('#64748b')
  const [font, setFont] = useState('Inter')
  const [socialLinks, setSocialLinks] = useState({})
  const [photoUrl, setPhotoUrl] = useState('')
  const [videoUrl, setVideoUrl] = useState('')
  const [products, setProducts] = useState([])
  const [services, setServices] = useState([])
  const [gallery, setGallery] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isUploadingProducts, setIsUploadingProducts] = useState(false)

  useEffect(() => {
    if (id === 'new') {
      const params = new URLSearchParams(location.search)
      const t = params.get('template')
      if (t && ['template-1','template-2','template-3','template-4'].includes(t)) {
        setSelectedTemplate(t)
      }
    }
  }, [id, location.search])

  useEffect(() => {
    if (id && id !== 'new') {
      fetchVCardById(id)
    }
  }, [id, fetchVCardById])

  useEffect(() => {
    if (currentVCard && id !== 'new') {
      // Populate form with existing data
      reset({
        name: currentVCard.name,
        position: currentVCard.position,
        phone: currentVCard.phone,
        email: currentVCard.email,
        bio: currentVCard.bio,
        slug: currentVCard.slug,
        public: currentVCard.public,
        nfcEnabled: currentVCard.nfcEnabled,
        location: currentVCard.location,
        address: currentVCard.address,
        lat: typeof currentVCard.lat === 'number' ? currentVCard.lat : undefined,
        lng: typeof currentVCard.lng === 'number' ? currentVCard.lng : undefined,
      })
      
      setSelectedTemplate(currentVCard.templateId || 'template-1')
      setPrimaryColor(currentVCard.theme?.primaryColor || '#2563eb')
      setAccentColor(currentVCard.theme?.accentColor || '#64748b')
      setFont(currentVCard.theme?.font || 'Inter')
      setSocialLinks(currentVCard.social || {})
      setPhotoUrl(currentVCard.photoUrl || '')
      setVideoUrl(currentVCard.videoUrl || '')
      
      // Convertir objetos de vuelta a arrays si es necesario
      if (currentVCard.products) {
        if (Array.isArray(currentVCard.products)) {
          setProducts(currentVCard.products)
        } else {
          // Si es objeto, convertir a array
          const productsArray = Object.entries(currentVCard.products).map(([id, product]) => ({
            id,
            ...product
          }))
          setProducts(productsArray)
        }
      } else {
        setProducts([])
      }
      
      if (currentVCard.services) {
        if (Array.isArray(currentVCard.services)) {
          setServices(currentVCard.services)
        } else {
          const servicesArray = Object.entries(currentVCard.services).map(([id, service]) => ({
            id,
            ...service
          }))
          setServices(servicesArray)
        }
      } else {
        setServices([])
      }
      
      if (currentVCard.gallery) {
        if (Array.isArray(currentVCard.gallery)) {
          setGallery(currentVCard.gallery)
        } else {
          const galleryArray = Object.entries(currentVCard.gallery).map(([id, item]) => ({
            id,
            ...item
          }))
          setGallery(galleryArray)
        }
      } else {
        setGallery([])
      }
    }
  }, [currentVCard, id, reset])

  const cleanArrays = () => {
    setProducts(prev => prev.filter(p => p && p.name && p.name.trim() !== ''))
    setServices(prev => prev.filter(s => s && s.title && s.title.trim() !== ''))
    setGallery(prev => prev.filter(g => g && g.imageUrl && g.imageUrl.trim() !== ''))
    toast.success('Arrays limpiados de elementos inválidos')
  }

  // Función para eliminar undefined de objetos profundamente
  const deepCleanUndefined = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(item => deepCleanUndefined(item)).filter(item => item !== undefined)
    } else if (obj && typeof obj === 'object') {
      const cleaned = {}
      for (const key in obj) {
        if (obj.hasOwnProperty(key) && obj[key] !== undefined) {
          cleaned[key] = deepCleanUndefined(obj[key])
        }
      }
      return cleaned
    }
    return obj
  }

  const forceDeleteVCard = async () => {
    if (!id || id === 'new') {
      toast.error('No hay Dkard para eliminar')
      return
    }
    
    if (window.confirm('¿Estás seguro de que quieres ELIMINAR permanentemente esta Dkard? Esta acción no se puede deshacer.')) {
      try {
        setIsSubmitting(true)
        await deleteVCard(id)
        toast.success('Dkard eliminada permanentemente')
        navigate('/editor')
      } catch (err) {
        console.error('Error al eliminar:', err)
        toast.error('Error al eliminar la Dkard: ' + err.message)
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const debugCurrentData = () => {
    const currentData = {
      name: watch('name'),
      slug: watch('slug'),
      position: watch('position'),
      phone: watch('phone'),
      email: watch('email'),
      bio: watch('bio'),
      public: watch('public'),
      templateId: selectedTemplate,
      products: products,
      services: services,
      gallery: gallery,
      social: socialLinks,
      theme: {
        primaryColor,
        accentColor,
        font
      }
    }
    
    console.log('=== DATOS ACTUALES EN EL FORMULARIO ===')
    console.log('Datos completos:', JSON.stringify(currentData, null, 2))
    console.log('Products:', products)
    console.log('Services:', services)
    console.log('Gallery:', gallery)
    
    // Copiar al portapapeles para que puedas verlos
    navigator.clipboard.writeText(JSON.stringify(currentData, null, 2))
      .then(() => toast.success('Datos copiados al portapapeles'))
      .catch(() => toast.info('Revisa la consola para ver los datos'))
    
    toast.info('Revisa la consola (F12) para ver todos los datos')
  }

  const saveWithSocialOnly = async () => {
    if (!user) {
      toast.error('Debes iniciar sesión')
      return
    }
    
    try {
      setIsSubmitting(true)
      
      // Datos con SOLO redes sociales - sin arrays grandes
      const socialData = {
        name: watch('name') || 'Mi Dkard',
        position: watch('position') || '',
        phone: watch('phone') || '',
        email: watch('email') || '',
        bio: watch('bio') || '',
        slug: watch('slug') || generateSlug(watch('name') || 'mi-dkard'),
        templateId: selectedTemplate || 'template-1',
        public: typeof watch('public') === 'boolean' ? watch('public') : false,
        nfcEnabled: typeof watch('nfcEnabled') === 'boolean' ? watch('nfcEnabled') : false,
        userId: user.uid,
        updatedAt: serverTimestamp(),
        theme: {
          primaryColor: primaryColor || '#2563eb',
          accentColor: accentColor || '#64748b',
          font: font || 'Inter'
        },
        // Solo redes sociales, sin otros arrays
        social: Object.fromEntries(
          Object.entries(socialLinks || {}).filter(([_, value]) => 
            value && typeof value === 'string' && value.trim() !== '' && value !== 'undefined'
          )
        ),
        // Arrays vacíos pero válidos
        products: [],
        services: [],
        gallery: []
      }
      
      console.log('=== GUARDANDO CON SOLO REDES SOCIALES ===')
      console.log('Datos:', socialData)
      console.log('Redes sociales:', socialData.social)
      
      if (id === 'new') {
        socialData.createdAt = serverTimestamp()
        
        const docRef = await addDoc(collection(db, 'vcards'), socialData)
        console.log('Dkard con redes sociales creada con ID:', docRef.id)
        toast.success('Dkard guardada con redes sociales')
        navigate(`/editor/${docRef.id}`)
      } else {
        await updateDoc(doc(db, 'vcards', id), socialData)
        console.log('Dkard con redes sociales actualizada con ID:', id)
        toast.success('Dkard actualizada con redes sociales')
      }
    } catch (err) {
      console.error('=== ERROR AL GUARDAR CON REDES SOCIALES ===')
      console.error('Error completo:', err)
      console.error('Mensaje:', err.message)
      
      toast.error(`ERROR redes sociales: ${err.message}`, {
        duration: 10000,
        position: 'top-center'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const saveWithoutArrays = async () => {
    if (!user) {
      toast.error('Debes iniciar sesión')
      return
    }
    
    try {
      setIsSubmitting(true)
      
      // Datos SIN arrays - solo información básica
      const basicData = {
        name: watch('name') || 'Mi Dkard',
        position: watch('position') || '',
        phone: watch('phone') || '',
        email: watch('email') || '',
        bio: watch('bio') || '',
        slug: watch('slug') || generateSlug(watch('name') || 'mi-dkard'),
        templateId: selectedTemplate || 'template-1',
        public: typeof watch('public') === 'boolean' ? watch('public') : false,
        nfcEnabled: typeof watch('nfcEnabled') === 'boolean' ? watch('nfcEnabled') : false,
        userId: user.uid,
        updatedAt: serverTimestamp(),
        theme: {
          primaryColor: primaryColor || '#2563eb',
          accentColor: accentColor || '#64748b',
          font: font || 'Inter'
        },
        social: Object.fromEntries(
          Object.entries(socialLinks || {}).filter(([_, value]) => 
            value && typeof value === 'string' && value.trim() !== ''
          )
        )
      }
      
      // IMPORTANTE: NO incluir products, services, ni gallery arrays
      console.log('=== GUARDANDO SIN ARRAYS ===')
      console.log('Datos básicos:', basicData)
      console.log('ID:', id)
      console.log('Es nuevo?', id === 'new')
      
      if (id === 'new') {
        basicData.createdAt = serverTimestamp()
        
        // Guardar directamente en Firestore SIN arrays
        const docRef = await addDoc(collection(db, 'vcards'), basicData)
        console.log('Dkard básica creada con ID:', docRef.id)
        toast.success('Dkard guardada sin arrays (solución temporal)')
        navigate(`/editor/${docRef.id}`)
      } else {
        // Actualizar directamente en Firestore SIN arrays
        await updateDoc(doc(db, 'vcards', id), basicData)
        console.log('Dkard básica actualizada con ID:', id)
        toast.success('Dkard actualizada sin arrays (solución temporal)')
      }
    } catch (err) {
      console.error('=== ERROR AL GUARDAR SIN ARRAYS ===')
      console.error('Error completo:', err)
      console.error('Mensaje:', err.message)
      console.error('Código:', err.code)
      console.error('Datos que fallaron:', basicData)
      
      toast.error(`ERROR sin arrays: ${err.message}`, {
        duration: 10000,
        position: 'top-center'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const saveEmergencyVCard = async () => {
    if (!user) {
      toast.error('Debes iniciar sesión')
      return
    }
    
    try {
      setIsSubmitting(true)
      
      // Datos de emergencia - absolutamente mínimos
      const emergencyData = {
        name: watch('name') || 'Dkard Sin Nombre',
        slug: watch('slug') || `dkard-${Date.now()}`,
        userId: user.uid,
        updatedAt: serverTimestamp(),
      }
      
      // Eliminar cualquier campo undefined antes de guardar
      Object.keys(emergencyData).forEach(key => {
        if (emergencyData[key] === undefined) {
          delete emergencyData[key]
        }
      })
      
      console.log('=== GUARDANDO DATOS DE EMERGENCIA ===')
      console.log('Datos:', emergencyData)
      console.log('ID:', id)
      console.log('Es nuevo?', id === 'new')
      
      // **GUARDAR SIN VERIFICAR LÍMITE** - Emergencia total
      if (id === 'new') {
        emergencyData.createdAt = serverTimestamp()
        
        // Guardar directamente en Firestore SIN verificar límite
        const docRef = await addDoc(collection(db, 'vcards'), emergencyData)
        console.log('Dkard de emergencia creada con ID:', docRef.id)
        toast.success('Dkard de emergencia creada (límite ignorado)')
        navigate(`/editor/${docRef.id}`)
      } else {
        // Actualizar directamente en Firestore
        await updateDoc(doc(db, 'vcards', id), emergencyData)
        console.log('Dkard de emergencia actualizada con ID:', id)
        toast.success('Dkard de emergencia actualizada')
      }
    } catch (err) {
      console.error('=== ERROR AL GUARDAR EMERGENCIA ===')
      console.error('Error completo:', err)
      console.error('Mensaje:', err.message)
      console.error('Código:', err.code)
      console.error('Datos que fallaron:', emergencyData)
      
      // Último recurso: mostrar exactamente qué está mal
      if (err.message) {
        toast.error(`ERROR DE EMERGENCIA: ${err.message}`, {
          duration: 10000,
          position: 'top-center'
        })
      } else {
        toast.error('Error crítico al guardar. Revisa la consola.', {
          duration: 8000,
          position: 'top-center'
        })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const saveMinimalVCard = async () => {
    if (!user) {
      toast.error('Debes iniciar sesión')
      return
    }
    
    try {
      setIsSubmitting(true)
      
      // Datos ultra-minimales - solo lo indispensable
      const ultraMinimalData = {
        name: watch('name') || 'Mi Dkard',
        slug: watch('slug') || generateSlug(watch('name') || 'mi-dkard'),
        templateId: selectedTemplate || 'template-1',
        userId: user.uid,
        updatedAt: serverTimestamp(),
      }
      
      // Eliminar cualquier campo undefined antes de guardar
      Object.keys(ultraMinimalData).forEach(key => {
        if (ultraMinimalData[key] === undefined) {
          delete ultraMinimalData[key]
        }
      })
      
      console.log('=== GUARDANDO DATOS ULTRA-MINIMALES ===')
      console.log('Datos:', ultraMinimalData)
      console.log('ID:', id)
      console.log('Es nuevo?', id === 'new')
      
      if (id === 'new') {
        ultraMinimalData.createdAt = serverTimestamp()
        
        // Guardar directamente en Firestore
        const docRef = await addDoc(collection(db, 'vcards'), ultraMinimalData)
        console.log('Nueva Dkard creada con ID:', docRef.id)
        toast.success('Dkard ultra-mínima creada exitosamente')
        navigate(`/editor/${docRef.id}`)
      } else {
        // Actualizar directamente en Firestore
        await updateDoc(doc(db, 'vcards', id), ultraMinimalData)
        console.log('Dkard actualizada con ID:', id)
        toast.success('Dkard ultra-mínima actualizada exitosamente')
      }
    } catch (err) {
      console.error('=== ERROR AL GUARDAR ULTRA-MÍNIMO ===')
      console.error('Error completo:', err)
      console.error('Mensaje:', err.message)
      console.error('Código:', err.code)
      console.error('Datos que fallaron:', ultraMinimalData)
      
      toast.error(`ERROR al guardar mínimo: ${err.message}`, {
        duration: 8000,
        position: 'top-center'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const onSubmit = async (data) => {
    if (!user) {
      toast.error('Debes iniciar sesión')
      return
    }

    setIsSubmitting(true)

    try {
      if (id === 'new') {
        const q = query(collection(db, 'vcards'), where('userId', '==', user.uid))
        const snap = await getDocs(q)
        const userDoc = await getDoc(doc(db, 'users', user.uid))
        const userData = userDoc.exists() ? userDoc.data() : {}
        const userMax = typeof userData.maxCards === 'number' ? userData.maxCards : (typeof user.maxCards === 'number' ? user.maxCards : 1)
        
        console.log('=== VERIFICANDO LÍMITE DE DKARDS ===')
        console.log('Usuario actual:', user.uid)
        console.log('Dkards encontradas:', snap.size)
        console.log('Límite permitido:', userMax)
        console.log('IDs encontradas:', snap.docs.map(d => ({id: d.id, name: d.data().name})))
        
        if (snap.size >= userMax) {
          toast.error(`Has alcanzado tu límite de Dkards (${userMax}).`, {
            duration: 5000,
            position: 'top-center'
          })
          
          // Mostrar las Dkards actuales para que el usuario las vea
          const existingCards = snap.docs.map(doc => ({id: doc.id, ...doc.data()}))
          console.log('Dkards existentes:', existingCards)
          
          toast(`Tienes ${snap.size} Dkard(s). El límite es ${userMax}.`, {
            duration: 5000
          })
          
          return
        }
      }
      const safeData = {
        ...data,
        phone: data.phone ?? '',
        email: data.email ?? '',
        public: typeof data.public === 'boolean' ? data.public : false,
      }

      if (safeData.lat !== undefined && !Number.isFinite(safeData.lat)) {
        safeData.lat = undefined
      }
      if (safeData.lng !== undefined && !Number.isFinite(safeData.lng)) {
        safeData.lng = undefined
      }
      if (safeData.lat !== undefined) {
        safeData.lat = Math.max(-90, Math.min(90, safeData.lat))
      }
      if (safeData.lng !== undefined) {
        safeData.lng = Math.max(-180, Math.min(180, safeData.lng))
      }

      // Convertir products a objeto plano (como redes sociales) para evitar problemas de Firestore
      const productsAsObject = {};
      if (Array.isArray(products)) {
        products.forEach((p, index) => {
          if (p && p.name && p.name.trim() !== '') {
            const productId = p.id || `product_${index}_${Date.now()}`;
            let imageUrl = '';
            
            // EVITAR base64 enormes que causan problemas en Firestore
            if (p.imageUrl && typeof p.imageUrl === 'string') {
              if (p.imageUrl.startsWith('data:')) {
                if (p.imageUrl.length > 10000) {
                  // Base64 demasiado grande - omitir por ahora
                  console.warn(`Imagen base64 demasiado grande (${p.imageUrl.length} chars) para producto ${p.name}, omitiendo`);
                  imageUrl = '';
                } else {
                  // Base64 pequeña está bien
                  imageUrl = p.imageUrl;
                }
              } else if (p.imageUrl.startsWith('http')) {
                // URLs externas están bien
                imageUrl = p.imageUrl;
              } else {
                imageUrl = p.imageUrl;
              }
            }
            
            productsAsObject[productId] = {
              name: String(p.name || ''),
              desc: p.desc ? String(p.desc) : '',
              price: (typeof p.price === 'number' && !isNaN(p.price))
                ? p.price
                : ((typeof p.price === 'string' && p.price.trim() !== '' && !isNaN(parseFloat(p.price))) ? parseFloat(p.price) : undefined),
              imageUrl: imageUrl,
              order: (typeof p.order === 'number' ? p.order : index)
            };
          }
        });
      }

      // Mismo approach para services y gallery
      const servicesAsObject = {};
      if (Array.isArray(services)) {
        services.forEach((s, index) => {
          if (s && s.title && s.title.trim() !== '') {
            const serviceId = s.id || `service_${index}_${Date.now()}`;
            servicesAsObject[serviceId] = {
              title: String(s.title || ''),
              desc: s.desc ? String(s.desc) : '',
              icon: s.icon ? String(s.icon) : '💼'
            };
          }
        });
      }

      const galleryAsObject = {};
      if (Array.isArray(gallery)) {
        gallery.forEach((g, index) => {
          if (g && g.imageUrl && g.imageUrl.trim() !== '') {
            const galleryId = g.id || `gallery_${index}_${Date.now()}`;
            galleryAsObject[galleryId] = {
              imageUrl: String(g.imageUrl || ''),
              caption: g.caption ? String(g.caption) : ''
            };
          }
        });
      }

      const servicesNormalized = Array.isArray(services)
        ? services.filter(s => s != null && typeof s === 'object').map(s => ({ 
            id: String(s.id || Date.now()), 
            title: String(s.title || ''), 
            desc: s.desc ? String(s.desc) : '', 
            icon: s.icon ? String(s.icon) : '💼' 
          })).filter(s => s.title && s.title.trim() !== '')
        : []

      const galleryNormalized = Array.isArray(gallery)
        ? gallery.filter(g => g != null && typeof g === 'object').map(g => ({ 
            id: String(g.id || Date.now()), 
            title: g.title ? String(g.title) : '', 
            imageUrl: g.imageUrl ? String(g.imageUrl) : '' 
          })).filter(g => g.imageUrl && g.imageUrl.trim() !== '')
        : []

      const vCardData = {
        ...safeData,
        templateId: selectedTemplate || 'template-1',
        theme: {
          primaryColor: primaryColor || '#2563eb',
          accentColor: accentColor || '#64748b',
          font: font || 'Inter',
        },
        social: Object.fromEntries(
          Object.entries(socialLinks || {}).filter(([_, value]) => 
            value && typeof value === 'string' && value.trim() !== ''
          )
        ),
        photoUrl: photoUrl || '',
        videoUrl: videoUrl && videoUrl.trim() !== '' ? videoUrl.trim() : undefined,
        products: productsAsObject,
        services: servicesAsObject,
        gallery: galleryAsObject,
        type: getTemplateType(selectedTemplate || 'template-1'),
        userId: user.uid,
        updatedAt: new Date().toISOString(),
      }
      
      // Eliminar cualquier campo undefined antes de guardar
      const cleanedVCardData = deepCleanUndefined(vCardData)

      // Debug: Verificar qué datos se envían
      console.log('Datos a guardar:', {
        products: cleanedVCardData.products,
        services: cleanedVCardData.services,
        gallery: cleanedVCardData.gallery,
        templateId: cleanedVCardData.templateId,
        productsType: typeof cleanedVCardData.products,
        servicesType: typeof cleanedVCardData.services,
        galleryType: typeof cleanedVCardData.gallery,
        productsKeys: cleanedVCardData.products ? Object.keys(cleanedVCardData.products) : 'none',
        servicesKeys: cleanedVCardData.services ? Object.keys(cleanedVCardData.services) : 'none',
        galleryKeys: cleanedVCardData.gallery ? Object.keys(cleanedVCardData.gallery) : 'none'
      })

      if (id === 'new') {
        cleanedVCardData.createdAt = new Date().toISOString()
        await createVCard(cleanedVCardData)
        toast.success('Dkard creada exitosamente')
      } else {
        // Para actualizaciones, usar updateDoc directamente para evitar problemas con datos existentes
        await updateDoc(doc(db, 'vcards', id), cleanedVCardData)
        toast.success('Dkard actualizada exitosamente')
      }

      navigate('/editor')
    } catch (err) {
      console.error('=== ERROR COMPLETO AL GUARDAR ===')
      console.error('Mensaje:', err.message)
      console.error('Código:', err.code)
      console.error('Detalles:', err.details)
      console.error('Stack:', err.stack)
      
      // Mostrar el error exacto de Firestore
      toast.error(`ERROR FIRESTORE: ${err.message || 'Error desconocido'}`, {
        duration: 8000,
        position: 'top-center'
      })
      
      // Si el error es muy específico, mostrar más detalles
      if (err.message?.includes('array contains')) {
        toast.error('Error: Un array contiene datos inválidos. Usa "Guardar Solo Datos Básicos"', {
          duration: 6000
        })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const getTemplateType = (templateId) => {
    const templateTypes = {
      'template-1': 'presentation',
      'template-2': 'services',
      'template-3': 'landing',
      'template-4': 'catalog',
    }
    return templateTypes[templateId] || 'presentation'
  }

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }

  const handleNameChange = (e) => {
    const name = e.target.value
    if (id === 'new' || !watch('slug')) {
      setValue('slug', generateSlug(name))
    }
  }

  if (loading && id !== 'new') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {id === 'new' ? 'Crear Nueva Dkard' : 'Editar Dkard'}
        </h1>
        <p className="text-gray-600">
          Personaliza tu tarjeta digital profesional
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Basic Information */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Información Básica</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre Completo *
              </label>
              <input
                type="text"
                {...register('name', { required: 'El nombre es requerido' })}
                onChange={handleNameChange}
                className="form-input"
                placeholder="Juan Pérez García"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cargo / Profesión *
              </label>
              <input
                type="text"
                {...register('position', { required: 'El cargo es requerido' })}
                className="form-input"
                placeholder="Desarrollador Web Senior"
              />
              {errors.position && (
                <p className="mt-1 text-sm text-red-600">{errors.position.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Teléfono
              </label>
              <input
                type="tel"
                {...register('phone')}
                className="form-input"
                placeholder="+591 700 00000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                {...register('email')}
                className="form-input"
                placeholder="juan@ejemplo.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ubicación (ciudad, zona)
              </label>
              <input
                type="text"
                {...register('location')}
                className="form-input"
                placeholder="La Paz, Sopocachi"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dirección completa
              </label>
              <input
                type="text"
                {...register('address')}
                className="form-input"
                placeholder="Av. Siempre Viva 742"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Latitud (opcional)
              </label>
              <input
                type="number"
                step="any"
                min="-90"
                max="90"
                {...register('lat', { valueAsNumber: true })}
                className="form-input"
                placeholder="-16.500000"
                onPaste={(e) => {
                  const text = e.clipboardData.getData('text')
                  const parts = text.trim().split(/[\s,]+/)
                  if (parts.length >= 2) {
                    const la = parseFloat(parts[0])
                    const lo = parseFloat(parts[1])
                    if (Number.isFinite(la)) setValue('lat', la)
                    if (Number.isFinite(lo)) setValue('lng', lo)
                    e.preventDefault()
                  }
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Longitud (opcional)
              </label>
              <input
                type="number"
                step="any"
                min="-180"
                max="180"
                {...register('lng', { valueAsNumber: true })}
                className="form-input"
                placeholder="-68.150000"
                onPaste={(e) => {
                  const text = e.clipboardData.getData('text')
                  const parts = text.trim().split(/[\s,]+/)
                  if (parts.length >= 2) {
                    const la = parseFloat(parts[0])
                    const lo = parseFloat(parts[1])
                    if (Number.isFinite(la)) setValue('lat', la)
                    if (Number.isFinite(lo)) setValue('lng', lo)
                    e.preventDefault()
                  }
                }}
              />
            </div>

            <div className="md:col-span-2">
              {(() => {
                const la = watch('lat')
                const lo = watch('lng')
                const loc = watch('location')
                const addr = watch('address')
                const url = (Number.isFinite(la) && Number.isFinite(lo))
                  ? `https://www.google.com/maps?q=${la},${lo}`
                  : ((loc || addr) ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc || addr)}` : '')
                return url ? (
                  <div className="flex items-center justify-between bg-gray-50 border rounded-lg p-3">
                    <span className="text-xs text-gray-700 truncate">{url}</span>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-3 px-3 py-1 rounded-md bg-blue-600 text-white text-xs hover:bg-blue-700"
                    >
                      Abrir mapa
                    </a>
                  </div>
                ) : null
              })()}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Biografía
              </label>
              <textarea
                {...register('bio')}
                rows={4}
                className="form-input"
                placeholder="Cuéntanos sobre ti, tu experiencia y lo que ofreces..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Slug (URL personalizada) *
              </label>
              <div className="flex">
                <input
                  type="text"
                  {...register('slug', { required: 'El slug es requerido' })}
                  className="form-input"
                  placeholder="juan-perez"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">Tu enlace será: {buildPublicUrl(watch('slug') || 'tu-slug')}</p>
              {errors.slug && (
                <p className="mt-1 text-sm text-red-600">{errors.slug.message}</p>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('public')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Hacer pública esta Dkard
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('nfcEnabled')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Habilitar compatibilidad NFC
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('pwaEnabled')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Habilitar PWA para esta Dkard (instalable)
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Template Selection */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Seleccionar Plantilla</h2>
          <TemplateSelector
            selectedTemplate={selectedTemplate}
            onTemplateChange={setSelectedTemplate}
          />
        </div>

        {/* Theme Customization */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Personalización Visual</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color Principal
              </label>
              <ColorPicker
                color={primaryColor}
                onColorChange={setPrimaryColor}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color de Acento
              </label>
              <ColorPicker
                color={accentColor}
                onColorChange={setAccentColor}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fuente
              </label>
              <select
                value={font}
                onChange={(e) => setFont(e.target.value)}
                className="form-input"
              >
                <option value="Inter">Inter</option>
                <option value="Roboto">Roboto</option>
                <option value="Open Sans">Open Sans</option>
                <option value="Lato">Lato</option>
                <option value="Montserrat">Montserrat</option>
              </select>
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Redes Sociales</h2>
          <SocialLinksForm
            socialLinks={socialLinks}
            onSocialLinksChange={setSocialLinks}
          />
        </div>

        {/* Profile Photo */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Foto de perfil</h2>
          <ProfilePhotoForm
            photoUrl={photoUrl}
            onChange={setPhotoUrl}
            userId={user?.uid}
            vcardId={id}
          />
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Video YouTube (opcional)</h2>
          <label className="block text-sm font-medium text-gray-700 mb-2">URL de video</label>
          <input
            type="text"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            className="form-input"
            placeholder="https://www.youtube.com/watch?v=... o https://youtu.be/..."
          />
        </div>

        {/* Products (if catalog template) */}
        {getTemplateType(selectedTemplate) === 'catalog' && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Productos</h2>
            <ProductsForm
              products={products}
              onProductsChange={setProducts}
              onUploadingChange={setIsUploadingProducts}
              userId={user?.uid}
              vcardId={id}
            />
          </div>
        )}

        {/* Services (if landing or services template) */}
        {(getTemplateType(selectedTemplate) === 'landing' || getTemplateType(selectedTemplate) === 'services') && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Servicios</h2>
            <ServicesForm services={services} onServicesChange={setServices} />
          </div>
        )}

        {/* Gallery (if landing template) */}
        {getTemplateType(selectedTemplate) === 'landing' && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Galería</h2>
            <GalleryForm 
              items={gallery} 
              onItemsChange={setGallery}
              userId={user?.uid}
              vcardId={id}
            />
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/editor')}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting || isUploadingProducts}
            disabled={isUploadingProducts}
          >
            {id === 'new' ? 'Crear Dkard' : 'Guardar Cambios'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default VCardEditorPage
